import { Chat, useChat, type UseChatHelpers } from "@ai-sdk/react";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { useRequiredContext } from "@redotech/react-util/context";
import type { PageContext } from "@redotech/redo-agents-common/page-context";
import { getCustomAnalyticsReportFilterOptionsQueryKey } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/hooks/use-custom-analytics-report-filter-options-query";
import { getCustomAnalyticsReportQueryInvalidationKey } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/hooks/use-custom-analytics-report-query";
import { getCustomAnalyticsReportRevisionsQueryInvalidationKey } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/hooks/use-custom-analytics-report-revisions-query";
import { getCustomAnalyticsReportRowsQueryInvalidationKey } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/hooks/use-custom-analytics-report-rows-query";
import { getCustomAnalyticsReportsQueryInvalidationKey } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/hooks/use-custom-analytics-reports-query";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import {
  GlobalAgentContext,
  type AgentClientToolExecutors,
  type AgentToolResultHandlers,
} from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";
import { LRUCache } from "@redotech/util/cache";
import { useQueryClient } from "@tanstack/react-query";
import { lastAssistantMessageIsCompleteWithApprovalResponses } from "ai";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clientToolResultsReadyToSend,
  dropClientToolRun,
  markClientToolDone,
  markClientToolRunning,
  wasClientToolCallStarted,
} from "../client-tools/client-tool-run-store";
import { cancelStaleToolPart } from "../conversion/cancel-stale-tool-part";
import { tryParseCustomAnalyticsReportAction } from "../custom-analytics-report-tool-actions";
import {
  appendMissingMessages,
  isEscalationInjectedMessageId,
  isHumanEscalationMessage,
} from "../escalation/intercom-to-uimessage";
import { toolDataChanges } from "../tool-data-changes";
import {
  AskRedoRpcTransport,
  type AskRedoEscalationCreatedEvent,
  type EscalationSnapshot,
} from "../transport/ask-redo-rpc-transport";
import type { AskRedoUIMessage } from "../types";
import {
  askRedoThreadHistoryMessages,
  loadAskRedoThreadHistory,
} from "./use-ask-redo-threads";

/**
 * Module-level cache of `Chat` instances, keyed by either a local
 * "new-<uuid>" id (for conversations that haven't been assigned a
 * server threadId yet) OR the server-assigned `LlmThread._id`.
 *
 * Why module-level: AI SDK's designed pattern for "move a thread
 * from one component to another" is to pass the SAME Chat instance
 * (via `useChat({ chat })`) to both components. A module cache lets
 * the slide-out and the full-page look up the same instance so the
 * user's conversation survives the transition (no re-hydration, no
 * lost in-flight stream, no `setMessages` dance).
 *
 * Sequence:
 *   1. Slide-out mounts. `chatId = "new-abc"`. Cache miss → factory
 *      creates Chat, stored at "new-abc".
 *   2. Server assigns threadId "T". The transport's
 *      `onThreadIdCaptured` callback re-keys the cached Chat under
 *      "T" AND removes the "new-abc" entry so it doesn't linger.
 *   3. User clicks Expand → navigates to /ask-redo/T.
 *   4. Full-page looks up "T" → cache hit → same Chat. Conversation
 *      survives the transition intact.
 *
 * LRU-capped via the shared `LRUCache` primitive — the cache
 * survives until the tab unloads, so unbounded growth is real.
 */
const CHAT_CACHE_CAPACITY = 20;
/**
 * Exported so the layout-level escalation watcher can push live human
 * messages into the right Chat instance (keyed by triageThreadId)
 * even when the slide-out UI is unmounted. The watcher is the
 * single SSE consumer; this cache is the seam between the data
 * layer and any mounted UI.
 */
export const chatInstances = new LRUCache<string, Chat<AskRedoUIMessage>>(
  CHAT_CACHE_CAPACITY,
);

/** The transport each cached Chat streams through; a later mount builds its own. */
const transportByChat = new WeakMap<
  Chat<AskRedoUIMessage>,
  AskRedoRpcTransport
>();

/** Turns each Chat already resumed (or found streaming); survives StrictMode re-runs. */
const resumedTurnsByChat = new WeakMap<Chat<AskRedoUIMessage>, Set<string>>();

function claimTurnResume(
  chat: Chat<AskRedoUIMessage>,
  turnId: string,
): boolean {
  const resumed = resumedTurnsByChat.get(chat) ?? new Set<string>();
  resumedTurnsByChat.set(chat, resumed);
  if (resumed.has(turnId)) {
    return false;
  }
  resumed.add(turnId);
  return true;
}

function isChatBusy(chat: Chat<AskRedoUIMessage>): boolean {
  return chat.status === "streaming" || chat.status === "submitted";
}

/** History first, then live support replies the escalation watcher injected that it lacks. */
function withInjectedEscalationMessages(
  history: AskRedoUIMessage[],
  existing: readonly AskRedoUIMessage[],
): AskRedoUIMessage[] {
  const historyIds = new Set(history.map((m) => m.id));
  return [
    ...history,
    ...existing.filter(
      (m) => isEscalationInjectedMessageId(m.id) && !historyIds.has(m.id),
    ),
  ];
}

/** After a worker restart the reply streamed so far is stale: reload the thread and replay the turn. */
async function rehydrateAndResumeTurn(
  rpcClient: RedoMerchantRpcClient,
  chatId: string,
  threadId: string,
): Promise<void> {
  const chat = chatInstances.get(threadId) ?? chatInstances.get(chatId);
  if (!chat) {
    return;
  }
  let history: AskRedoUIMessage[];
  try {
    history = askRedoThreadHistoryMessages(
      await loadAskRedoThreadHistory(rpcClient, threadId),
    );
  } catch (error) {
    console.error(
      "[ask-redo] reload after turn restart failed",
      { threadId },
      error,
    );
    return;
  }
  if (isChatBusy(chat)) {
    return;
  }
  chat.messages = withInjectedEscalationMessages(history, chat.messages);
  void chat.resumeStream();
}

/**
 * OUTBOUND tool-result seam. A tool like `update-portal-customization` streams
 * a patch as its output; the portal builder page registers a handler (keyed by
 * tool name in GlobalAgentContext) that applies that patch to its local draft.
 * The AI-SDK pipeline has no equivalent of the legacy chat's `onToolResult`
 * push, so we bridge it through the Chat instance's native `onFinish`. Generic
 * by design — any tool whose name has a registered handler fires.
 *
 * Why `onFinish` rather than an effect that scans `chat.messages`:
 *  - It fires exactly once per finished assistant turn, and only for freshly
 *    STREAMED messages — never for messages injected via `setMessages` during
 *    thread hydration. So reopening a thread that contains a historical portal
 *    patch does NOT re-apply it over the merchant's current draft.
 *  - It's bound to the Chat instance at construction, so the slide-out and
 *    full-page mounts that share one cached Chat can't double-apply — no
 *    per-instance dedupe bookkeeping needed.
 */
export function applyFinishedToolResults(
  message: AskRedoUIMessage,
  handlers: AgentToolResultHandlers | undefined,
  options?: {
    onCustomAnalyticsReportChanged?: () => void;
    onDataChanged?: (toolNames: readonly string[]) => void;
  },
): void {
  let customAnalyticsReportChanged = false;
  const dataChanges = new Set<string>();

  for (const part of message.parts) {
    if (part.type !== "dynamic-tool" || part.state !== "output-available") {
      continue;
    }
    handlers?.[part.toolName]?.(part.output);
    for (const name of toolDataChanges(part.toolName, part.output)) {
      dataChanges.add(name);
    }
    customAnalyticsReportChanged ||= isCustomAnalyticsReportChangeOutput({
      toolName: part.toolName,
      output: part.output,
    });
  }

  if (dataChanges.size > 0) {
    options?.onDataChanged?.([...dataChanges]);
  }

  if (customAnalyticsReportChanged) {
    options?.onCustomAnalyticsReportChanged?.();
  }
}

function isCustomAnalyticsReportChangeOutput({
  toolName,
  output,
}: {
  toolName: string;
  output: unknown;
}): boolean {
  return (
    tryParseCustomAnalyticsReportAction(toolName, output)?.type ===
    "view-report"
  );
}

/**
 * Module-level "latest" page context + timezone. Written inside a
 * post-render effect by the active mount, read by every cached
 * transport at send time.
 *
 * Why module-level (not per-hook ref): `Chat` instances are cached
 * across mounts. Their transport closes over whatever ref existed
 * at the mount that CREATED the Chat. A later mount has a brand-new
 * ref the cached transport never sees, so per-mount refs go stale
 * across the slide-out → full-page handoff. Module-level publish
 * solves this: any mount's write is visible to any cached transport.
 *
 * Why in an effect (not during render): writing during render
 * violates React's render-purity contract — a discarded render
 * (Suspense replay, transition) would still publish, possibly
 * clobbering a live mount's values. Effects only run after a
 * committed render, which is exactly the invariant we need.
 */
let latestPageContext: PageContext | undefined;
let latestMerchantTimezone: string | undefined;
let latestEscalationSnapshot: EscalationSnapshot | undefined;
/**
 * Latest page-registered tool-result handlers, published module-level so the
 * cached Chat's `onFinish` (set once at construction) always reads the current
 * map. Same rationale as `latestPageContext`: the Chat instance outlives the
 * mount that created it, so a per-mount closure would go stale.
 */
let latestToolResultHandlers: AgentToolResultHandlers | undefined;
let latestInvalidateCustomAnalyticsReports: (() => void) | undefined;
let latestOnDataChanged: ((toolNames: readonly string[]) => void) | undefined;
/**
 * Latest page-registered client-tool executors, published module-level for
 * the cached Chat's `onToolCall` (set once at construction) — same staleness
 * rationale as `latestToolResultHandlers`.
 */
let latestClientToolExecutors: AgentClientToolExecutors | undefined;

/**
 * Where cached transports report server-created escalations. Published by
 * the layout-level escalation watcher, which always sits under the live
 * EscalationProvider: the provider remounts with the Page shell, so the
 * setter of the mount that created a Chat can belong to an unmounted one.
 */
export interface AskRedoEscalationSink {
  bindCreatedEscalation: (event: AskRedoEscalationCreatedEvent) => void;
  /** Re-reads active escalations and binds the one on `handoffThreadId`. */
  refreshActiveEscalations: (handoffThreadId: string | undefined) => void;
}
let latestEscalationSink: AskRedoEscalationSink | undefined;

export function publishAskRedoEscalationSink(
  sink: AskRedoEscalationSink,
): () => void {
  latestEscalationSink = sink;
  return () => {
    if (latestEscalationSink === sink) {
      latestEscalationSink = undefined;
    }
  };
}

function hasEscalateToHumanOutput(message: AskRedoUIMessage): boolean {
  return message.parts.some(
    (part) =>
      part.type === "dynamic-tool" &&
      part.toolName === "escalate-to-human" &&
      part.state === "output-available",
  );
}

/**
 * Executes a browser-side tool call if the current page registered an
 * executor for it. Marks the run in the client-tool store FIRST so the
 * `sendAutomaticallyWhen` predicate (evaluated synchronously inside
 * `addToolOutput`) sees a consistent picture, then lands the output on the
 * chat's tool part — which triggers the auto-resume once every tracked run
 * has finished.
 */
export function runClientToolCall(
  chat: Pick<Chat<AskRedoUIMessage>, "addToolOutput">,
  toolCall: { toolCallId: string; toolName: string; input: unknown },
  executors: AgentClientToolExecutors | undefined,
): Promise<void> | undefined {
  const executor = executors?.[toolCall.toolName];
  if (!executor) {
    // Server-executed tool (or the owning page is unmounted) — not ours.
    return undefined;
  }
  if (wasClientToolCallStarted(toolCall.toolCallId)) {
    return undefined;
  }
  markClientToolRunning(toolCall.toolCallId, toolCall.toolName);
  return (async () => {
    try {
      const output = await executor(toolCall.input);
      markClientToolDone(toolCall.toolCallId);
      try {
        await chat.addToolOutput({
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          output,
        });
      } catch (error) {
        dropClientToolRun(toolCall.toolCallId);
        console.warn("[ask-redo] dropped orphaned client tool output", {
          error,
          toolCallId: toolCall.toolCallId,
          toolName: toolCall.toolName,
        });
      }
    } catch (error) {
      markClientToolDone(toolCall.toolCallId);
      try {
        await chat.addToolOutput({
          state: "output-error",
          tool: toolCall.toolName,
          toolCallId: toolCall.toolCallId,
          errorText: error instanceof Error ? error.message : String(error),
        });
      } catch (addOutputError) {
        dropClientToolRun(toolCall.toolCallId);
        console.warn("[ask-redo] dropped orphaned client tool error", {
          error: addOutputError,
          toolCallId: toolCall.toolCallId,
          toolName: toolCall.toolName,
        });
      }
    }
  })();
}

/**
 * Wraps `useChat` with the Ask Redo RPC transport + auto-resume-
 * after-approval semantics, sharing `Chat` instances across mounts
 * via the module cache above so state survives slide-out→full-page
 * navigation.
 *
 * `initialThreadId` is the server-assigned `LlmThread._id` if the
 * caller is hydrating an existing thread (deep-link / expand). When
 * present it's both the cache key and the initial threadId state.
 * The hook generates its own stable local id for fresh
 * conversations — callers shouldn't be juggling `chatId`s themselves.
 *
 * Return shape:
 *   - `chat` — `UseChatHelpers` (sendMessage, messages, status, etc.)
 *   - `threadId` — current server thread id. Starts as
 *     `initialThreadId` and flips to the real value after the first
 *     `complete` event.
 *   - `isStreaming` — convenience derivation of
 *     `chat.status === "streaming" || "submitted"`. Returned here so
 *     callers (page, slide-out, conversation) don't re-derive it
 *     three different times.
 *   - `stop` — Stop button: stops the turn on the server, then locally.
 *
 * `activeTurn` comes with the thread history; when the Chat isn't
 * already streaming it, the hook reattaches and replays the turn.
 */
export function useAskRedoChat(
  initialThreadId?: string,
  initialMessages?: AskRedoUIMessage[],
  activeTurn?: { turnId: string },
): {
  chat: UseChatHelpers<AskRedoUIMessage>;
  threadId: string | undefined;
  isStreaming: boolean;
  stop: () => void;
  requestEmergencyEscalation: () => Promise<void>;
  emergencyEscalationPending: boolean;
  emergencyEscalationError: string | null;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const queryClient = useQueryClient();
  const reloadTeam = useContext(ReloadTeamContext);
  const {
    pageContext,
    toolResultHandlers,
    clientToolExecutors,
    onPageDataChanged,
  } = useContext(GlobalAgentContext);
  const onDataChanged = useCallback(
    (toolNames: readonly string[]) => {
      reloadTeam?.();
      onPageDataChanged?.(toolNames);
    },
    [reloadTeam, onPageDataChanged],
  );
  const { escalation, setTriageThreadId } = useEscalationContext();
  const invalidateCustomAnalyticsReports = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportsQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportFilterOptionsQueryKey(team._id),
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportRowsQueryInvalidationKey(team._id),
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: getCustomAnalyticsReportRevisionsQueryInvalidationKey(team._id),
      exact: false,
    });
  }, [queryClient, team._id]);

  // Stable per-mount local id used when there's no server threadId
  // yet. Initialised from `initialThreadId` when present so a
  // deep-link straight to /ask-redo/T hits the cache by T from the
  // very first render.
  const [chatId] = useState(
    () => initialThreadId ?? `new-${crypto.randomUUID()}`,
  );
  const [threadId, setThreadId] = useState<string | undefined>(initialThreadId);
  const [emergencyEscalationPending, setEmergencyEscalationPending] =
    useState(false);
  const [emergencyEscalationError, setEmergencyEscalationError] = useState<
    string | null
  >(null);
  const emergencyEscalationInFlightRef = useRef(false);

  // Mirror the chat's server-assigned threadId into EscalationContext
  // so the escalate-to-human card has a triageThreadId to attach the
  // Intercom conversation to. The slide-out's "fresh conversation"
  // effect intentionally clears it on open; we re-populate as soon as
  // the agent assigns a real LlmThread._id.
  useEffect(() => {
    if (threadId) {
      setTriageThreadId(threadId);
    }
  }, [threadId, setTriageThreadId]);

  // Publish the latest page context + timezone + escalation snapshot
  // for any cached transport to read on its next `sendMessages` call.
  // Effect so it only runs after the render commits.
  useEffect(() => {
    latestPageContext = pageContext;
    latestMerchantTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [pageContext]);

  // Publish latest callbacks for the cached Chat's `onFinish` to read.
  // Effect so it only runs after the render commits.
  useEffect(() => {
    latestToolResultHandlers = toolResultHandlers;
    latestOnDataChanged = onDataChanged;
    latestInvalidateCustomAnalyticsReports = invalidateCustomAnalyticsReports;
    latestClientToolExecutors = clientToolExecutors;
  }, [
    toolResultHandlers,
    onDataChanged,
    invalidateCustomAnalyticsReports,
    clientToolExecutors,
  ]);

  useEffect(() => {
    latestEscalationSnapshot = {
      status: escalation.status,
      escalationId: escalation.escalationId,
      // Publish the bound triageThreadId (NOT the floating
      // `EscalationContext.triageThreadId` setter, which gets
      // overwritten every time the merchant clicks a different
      // history thread). The transport scopes diversion to the
      // chat bound to this exact thread — without it, a fresh
      // chat on a different thread would still route into the
      // active Intercom conversation.
      triageThreadId: escalation.triageThreadId,
    };
  }, [escalation.status, escalation.escalationId, escalation.triageThreadId]);

  // Stable setter ref so the transport's useMemo doesn't invalidate
  // each render (which would reset its `threadIdByChatId` map).
  const setThreadIdRef = useRef(setThreadId);
  setThreadIdRef.current = setThreadId;

  // Preferred cache key: real threadId if we have one, else the
  // local chatId. When the server assigns a threadId mid-session
  // the transport callback below re-keys the existing Chat under
  // the new key AND drops the old `new-<uuid>` entry.
  const cacheKey = threadId ?? chatId;

  const transport = useMemo(
    () =>
      new AskRedoRpcTransport(rpcClient, {
        onThreadIdCaptured: (capturedForChatId, tid) => {
          setThreadIdRef.current(tid);
          const existing = chatInstances.get(capturedForChatId);
          if (existing) {
            // Re-key the cached Chat under the server threadId.
            // Drop the `new-<uuid>` entry — keeping it would leave
            // a permanent zombie reference to the same Chat (and
            // double the cache footprint per thread).
            if (capturedForChatId !== tid) {
              chatInstances.delete(capturedForChatId);
            }
            chatInstances.set(tid, existing);
          }
        },
        getPageContext: () => latestPageContext,
        getMerchantTimezone: () => latestMerchantTimezone,
        getEscalation: () => latestEscalationSnapshot,
        onEscalationCreated: (event) => {
          latestEscalationSink?.bindCreatedEscalation(event);
        },
        onTurnRestarted: (restartedChatId, restartedThreadId) => {
          void rehydrateAndResumeTurn(
            rpcClient,
            restartedChatId,
            restartedThreadId,
          );
        },
        onStaleToolCancelled: ({ chatId, threadId, toolCallId, errorText }) => {
          // Stream chunks can't touch earlier messages; patch the cached
          // Chat directly (same pattern as AskRedoEscalationWatcher).
          const instance =
            (threadId ? chatInstances.get(threadId) : undefined) ??
            chatInstances.get(chatId);
          if (!instance) {
            return;
          }
          instance.messages = cancelStaleToolPart(
            instance.messages,
            toolCallId,
            errorText,
          );
        },
      }),
    [rpcClient],
  );

  const chatInstance = useMemo(() => {
    const existing = chatInstances.get(cacheKey);
    if (existing) {
      return existing;
    }
    const fresh = new Chat<AskRedoUIMessage>({
      id: cacheKey,
      messages: initialMessages,
      transport,
      // Auto-resume the stream when (a) the merchant answered every tool
      // approval, or (b) every browser-executed tool call has landed its
      // output (see client-tool-run-store) — both are "the turn is ours
      // to send back" conditions.
      sendAutomaticallyWhen: (options) =>
        lastAssistantMessageIsCompleteWithApprovalResponses(options) ||
        clientToolResultsReadyToSend(options.messages),
      onToolCall: ({ toolCall }) => {
        void runClientToolCall(fresh, toolCall, latestClientToolExecutors);
      },
      onFinish: ({ message }) => {
        // The restarted turn's replay finishes again with the kept reply.
        if (transport.takeSupersededFinish(fresh.id)) {
          return;
        }
        applyFinishedToolResults(message, latestToolResultHandlers, {
          onDataChanged: latestOnDataChanged,
          onCustomAnalyticsReportChanged:
            latestInvalidateCustomAnalyticsReports,
        });
        // Backstop for a handoff whose escalation event never arrived.
        if (hasEscalateToHumanOutput(message)) {
          latestEscalationSink?.refreshActiveEscalations(
            transport.getThreadIdForChat(fresh.id),
          );
        }
      },
    });
    chatInstances.set(cacheKey, fresh);
    transportByChat.set(fresh, transport);
    return fresh;
    // `initialMessages` intentionally NOT in deps — it's captured
    // at construction and re-applied via the hydration effect
    // below if it arrives late. Including it would blow away the
    // cached Chat on every re-render that produced a new
    // initialMessages array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, transport]);

  const chat = useChat<AskRedoUIMessage>({ chat: chatInstance });

  const stop = useCallback(() => {
    (transportByChat.get(chatInstance) ?? transport).stopTurn(chatInstance.id);
    void chatInstance.stop();
  }, [chatInstance, transport]);

  const requestEmergencyEscalation = useCallback(async () => {
    if (emergencyEscalationInFlightRef.current) {
      return;
    }
    emergencyEscalationInFlightRef.current = true;
    setEmergencyEscalationPending(true);
    setEmergencyEscalationError(null);
    try {
      await transport.requestEmergencyEscalation({ chatId });
    } catch (error) {
      console.error("[ask-redo] emergency support handoff failed", error);
      setEmergencyEscalationError(
        error instanceof Error
          ? error.message
          : "Failed to connect to support.",
      );
    } finally {
      emergencyEscalationInFlightRef.current = false;
      setEmergencyEscalationPending(false);
    }
  }, [chatId, transport]);

  // Completed tool results are dispatched to page-registered handlers via the
  // Chat instance's `onFinish` (see `applyFinishedToolResults`), not here.

  // Late hydration: for the "direct URL load" path (no slide-out
  // predecessor → cache miss), the Chat was created with empty
  // messages because `useAskRedoThreadHistory` loads asynchronously.
  // Inject the messages once they arrive — but only once per Chat
  // instance, gated by a ref so a user typing during the load
  // can't be clobbered by a late-resolving fetch.
  const hydratedChatsRef = useRef<WeakSet<Chat<AskRedoUIMessage>>>(
    new WeakSet(),
  );
  useEffect(() => {
    if (!initialMessages || initialMessages.length === 0) {
      return;
    }
    if (hydratedChatsRef.current.has(chatInstance)) {
      return;
    }
    hydratedChatsRef.current.add(chatInstance);
    // If the Chat already holds any "real" (non-escalation-injected)
    // message, keep its transcript: streamed or typed ids never match the
    // fetched history's, so merging would duplicate it. Append only rep
    // replies (deterministic `escalation-human-` ids), which arrive only
    // here when the escalation stream was down. Merchant replies typed here
    // carry useChat ids, so their `escalation-user-` copies are skipped.
    //
    // The only state we hydrate underneath is a cached Chat that holds
    // ONLY live human replies the layout-level AskRedoEscalationWatcher
    // injected (ids `escalation-human-`/`escalation-user-`) before
    // `getAskRedoThread` returned — the race that left human-active
    // escalations blank. Those carry no AI transcript, so the history is
    // merged in under them, deduped by id against any escalation message
    // the history already contains, matching the `[...ai, ...escalation]`
    // ordering `useAskRedoThreadHistory` produces.
    //
    // Read the LIVE `chatInstance.messages`, not the `chat.messages`
    // useChat snapshot: the watcher mutates the instance directly outside
    // React's render cycle, so a reply that lands after this render but
    // before this effect runs is only visible on the instance. Reading
    // the stale snapshot could miss it and then overwrite it (the
    // `hydratedChatsRef` guard would lock that loss in).
    const existingMessages = chatInstance.messages;
    const hasRealConversation = existingMessages.some(
      (m) => !isEscalationInjectedMessageId(m.id),
    );
    if (hasRealConversation) {
      const merged = appendMissingMessages(
        existingMessages,
        initialMessages.filter(isHumanEscalationMessage),
      );
      if (merged !== existingMessages) {
        chat.setMessages(merged);
      }
      return;
    }
    const existingIds = new Set(existingMessages.map((m) => m.id));
    const missingHistory = initialMessages.filter(
      (m) => !existingIds.has(m.id),
    );
    if (missingHistory.length === 0) {
      return;
    }
    chat.setMessages([...missingHistory, ...existingMessages]);
  }, [initialMessages, chatInstance, chat]);

  // Reattach to a turn still running on the server (reload, second tab); a Chat
  // holding messages the history lacks is this tab's own live view, left alone.
  const activeTurnId = activeTurn?.turnId;
  useEffect(() => {
    if (!activeTurnId || !initialMessages) {
      return;
    }
    const historyIds = new Set(initialMessages.map((m) => m.id));
    const ownsConversation = chatInstance.messages.some(
      (m) => !isEscalationInjectedMessageId(m.id) && !historyIds.has(m.id),
    );
    if (
      ownsConversation ||
      isChatBusy(chatInstance) ||
      !claimTurnResume(chatInstance, activeTurnId)
    ) {
      return;
    }
    chatInstance.messages = withInjectedEscalationMessages(
      initialMessages,
      chatInstance.messages,
    );
    void chatInstance.resumeStream();
  }, [activeTurnId, initialMessages, chatInstance]);

  // Live human-reply injection lives in AskRedoEscalationWatcher
  // (layout level). It looks up THIS Chat instance from
  // `chatInstances` keyed by triageThreadId and assigns
  // `chat.messages = [...]` (the AbstractChat setter — `setMessages`
  // is only on the useChat hook return, not on the instance). The
  // ReactChatState setter fires subscribed useSyncExternalStore
  // callbacks so every mounted useChat re-renders with the appended
  // message — works whether the slide-out is mounted or not. The
  // agentName + agentAvatarUrl are exposed via
  // EscalationAuthorContext, so the banner and message bubble
  // renderers read them via a hook.

  const isStreaming =
    chat.status === "streaming" || chat.status === "submitted";

  return {
    chat,
    threadId,
    isStreaming,
    stop,
    requestEmergencyEscalation,
    emergencyEscalationPending,
    emergencyEscalationError,
  };
}
